import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useI18n, UI_LANGS } from "../i18n/LanguageContext";
import { switchLangPath } from "../lib/routes";
import { track, EVENTS } from "../lib/analytics";

const DISMISS_KEY = "ep_lang_suggest_dismissed_v1";

// Copy shown in the VISITOR's browser language so they understand it.
const COPY = {
  en: { text: "This page is also available in English.", btn: "View in English" },
  es: { text: "Esta página también está disponible en español.", btn: "Ver en español" },
  fr: { text: "Cette page est aussi disponible en français.", btn: "Voir en français" },
  de: { text: "Diese Seite ist auch auf Deutsch verfügbar.", btn: "Auf Deutsch ansehen" },
  it: { text: "Questa pagina è disponibile anche in italiano.", btn: "Vedi in italiano" },
  pt: { text: "Esta página também está disponível em português.", btn: "Ver em português" },
};

export default function LangSuggest() {
  const { lang, changeLang } = useI18n();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);

  useEffect(() => {
    if (pathname.startsWith("/blueprint-os")) return;
    let browser;
    try {
      browser = (navigator.language || "").toLowerCase().slice(0, 2);
      if (localStorage.getItem(DISMISS_KEY)) return;
      if (!localStorage.getItem("ep_cookie_consent_v1")) return; // wait until cookie banner is resolved
    } catch {
      return;
    }
    if (!UI_LANGS.includes(browser) || browser === lang) {
      setTarget(null);
      return;
    }
    const t = setTimeout(() => setTarget(browser), 1200);
    return () => clearTimeout(t);
  }, [lang, pathname]);

  if (!target || target === lang) return null;
  const c = COPY[target];

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
    setTarget(null);
  };

  const accept = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
    track(EVENTS.LANGUAGE_CHANGED, { from: lang, to: target, source: "geo_suggest" });
    const path = switchLangPath(pathname, target);
    changeLang(target);
    navigate(path);
    setTarget(null);
  };

  return (
    <div data-testid="lang-suggest-banner"
      className="fixed bottom-4 left-4 z-[95] max-w-[calc(100vw-2rem)] animate-in fade-in slide-in-from-bottom-3 duration-500 sm:max-w-sm">
      <div className="flex items-center gap-3 rounded-2xl border border-black/8 bg-white/95 py-3 pl-4 pr-2 shadow-xl backdrop-blur-md">
        <p className="text-xs font-light leading-snug text-[#2a2b29]">{c.text}</p>
        <button data-testid="lang-suggest-accept" onClick={accept}
          className="shrink-0 whitespace-nowrap rounded-full bg-[#050505] px-3.5 py-2 text-[11px] font-medium text-white transition-opacity hover:opacity-85">
          {c.btn}
        </button>
        <button data-testid="lang-suggest-dismiss" onClick={dismiss} aria-label="Dismiss"
          className="shrink-0 rounded-full p-1.5 text-[#9a9d98] transition-colors hover:bg-black/[0.05]">
          <X size={13} />
        </button>
      </div>
    </div>
  );
}
